import { Link } from "react-router-dom";
import { Hexagon, ArrowLeft, Cookie } from "lucide-react";
import { Button } from "@/components/ui/button";
import { openConsentBanner } from "@/lib/cookieConsent";

export default function CookiePolicy() {
  return (
    <div className="min-h-screen bg-stone-50" data-testid="cookie-policy-page">
      <header className="max-w-4xl mx-auto flex items-center justify-between px-6 py-5">
        <Link to="/" className="flex items-center gap-2" data-testid="cookie-policy-home-link">
          <div className="w-9 h-9 rounded-xl bg-yellow-400 grid place-items-center">
            <Hexagon className="w-5 h-5 text-amber-900" strokeWidth={2.5} />
          </div>
          <span className="font-display text-xl font-semibold tracking-tight">
            Hive <span className="text-yellow-500">@</span> Buzzzillion
          </span>
        </Link>
        <Link to="/">
          <Button variant="ghost" className="rounded-full" data-testid="cookie-policy-back-button">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back
          </Button>
        </Link>
      </header>

      <main className="max-w-3xl mx-auto px-6 pb-20">
        <div className="text-xs font-semibold uppercase tracking-[0.2em] text-stone-500">Legal</div>
        <h1 className="font-display text-4xl sm:text-5xl font-light tracking-tight mt-2">
          Cookie Policy
        </h1>
        <p className="mt-3 text-sm text-stone-500">
          Last updated: {new Date().toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })}
        </p>

        <div className="mt-8 bg-white border border-stone-200 rounded-3xl p-6 sm:p-10 space-y-8 text-stone-700 leading-relaxed">
          <section>
            <h2 className="font-display text-2xl text-stone-900">1. What are cookies?</h2>
            <p className="mt-2">
              Cookies are small text files placed on your device when you visit a website.
              They allow the site to remember information about your visit — such as your
              preferences and interactions — and help us understand how visitors use the
              site so we can improve it. We also use closely-related technologies such as
              browser <em>localStorage</em>, <em>sessionStorage</em>, and lightweight
              first-party analytics beacons; for simplicity we refer to all of these
              collectively as &ldquo;cookies&rdquo; throughout this policy.
            </p>
          </section>

          <section>
            <h2 className="font-display text-2xl text-stone-900">2. How we use cookies</h2>
            <p className="mt-2">
              Hive @ Buzzzillion uses cookies for two purposes only:
            </p>
            <ul className="list-disc pl-6 mt-2 space-y-2">
              <li>
                <strong>Strictly necessary.</strong> To keep you signed in, remember
                your cookie preference, and make the site work safely. These cannot be
                turned off.
              </li>
              <li>
                <strong>Performance & analytics.</strong> To measure aggregate, anonymous
                page-view activity so we can spot broken pages, slow areas, and improve
                the product. This is the category controlled by the consent banner.
              </li>
            </ul>
            <p className="mt-2">
              We do <strong>not</strong> use cookies for advertising, cross-site tracking,
              or selling data to third parties.
            </p>
          </section>

          <section>
            <h2 className="font-display text-2xl text-stone-900">3. Your choices</h2>
            <p className="mt-2">
              The first time you visit the site, a banner appears at the bottom of the
              window asking for your consent.
            </p>
            <ul className="list-disc pl-6 mt-2 space-y-2">
              <li>
                <strong>Accept</strong> (the default — also triggered by clicking anywhere
                on the banner): we store your preference in your browser and continue
                to collect anonymous performance analytics.
              </li>
              <li>
                <strong>Deny</strong>: we immediately stop collecting performance
                analytics for the remainder of this browser session. Because the choice
                is session-scoped, the banner will reappear the next time you visit so
                you can revise your decision.
              </li>
            </ul>
            <p className="mt-2">
              You can re-open the banner at any time using the
              <em> &ldquo;Manage cookie preferences&rdquo; </em>
              link in the site footer, or by clicking the button below.
            </p>
            <div className="mt-4">
              <button
                type="button"
                onClick={openConsentBanner}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold bg-yellow-400 text-amber-900 hover:bg-yellow-300 transition"
                data-testid="cookie-policy-open-banner"
              >
                <Cookie className="w-4 h-4" /> Manage cookie preferences
              </button>
            </div>
          </section>

          <section>
            <h2 className="font-display text-2xl text-stone-900">4. Browser controls</h2>
            <p className="mt-2">
              In addition to the banner, every modern browser lets you view, block, or
              delete cookies. Refer to your browser&rsquo;s help pages — typically under
              Settings → Privacy — for instructions. Blocking strictly-necessary cookies
              may prevent parts of the site (such as sign-in) from working correctly.
            </p>
          </section>

          <section>
            <h2 className="font-display text-2xl text-stone-900">5. Changes to this policy</h2>
            <p className="mt-2">
              We may update this Cookie Policy from time to time to reflect changes in
              technology, regulation, or our practices. When we do, we will update the
              &ldquo;Last updated&rdquo; date at the top of this page. Continued use of the site
              after a change indicates acceptance of the revised policy.
            </p>
          </section>

          <section>
            <h2 className="font-display text-2xl text-stone-900">6. Contact</h2>
            <p className="mt-2">
              Questions about this Cookie Policy? Reach us through the in-app
              <em> Support </em> dialog or via the contact links on the home page.
            </p>
          </section>
        </div>

        <div className="mt-6 text-xs text-stone-500">
          See also our{" "}
          <Link to="/policy" className="underline hover:text-stone-700">
            Terms of Use & Content Policy
          </Link>
          .
        </div>
      </main>
    </div>
  );
}
